import { yupResolver } from '@hookform/resolvers/yup';
import { EmployerDto } from '@zorro/clients';
import {
  UNEXPECTED_ERROR_MESSAGE,
  VALIDATION_MESSAGES,
  isDefined,
} from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { Stack } from '@zorro/web/design-system';
import {
  ErrorsTable,
  FormFooter,
  RecordForActions,
} from '@zorro/web/shared-ui';
import {
  showErrorNotification,
  useBatchCallEndpoint,
  useForm,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import * as yup from 'yup';

export interface AssignEnrollmentsDrawerProps {
  employerId?: EmployerDto['id'];
  associatedEnrollmentTeamId?: string | null;
  associatedEnrollmentTeamName?: string | null;
  recordsForActions: RecordForActions[];
  onClose: () => void;
}

const AssignEnrollmentSchema = yup.object().shape({
  enrollmentTeamId: yup
    .string()
    .required(VALIDATION_MESSAGES.enrollmentTeamRequired),
  enrollmentAgentId: yup.string().notRequired(),
});

type AssignEnrollmentsFormFields = yup.InferType<typeof AssignEnrollmentSchema>;

export const AssignEnrollmentForm = ({
  employerId,
  associatedEnrollmentTeamId,
  associatedEnrollmentTeamName,
  recordsForActions,
  onClose,
}: AssignEnrollmentsDrawerProps) => {
  const { isZorroOperations } = useRoles();

  const isSingleEmployee = recordsForActions.length === 1;

  const isCrossEmployerSharedTeam =
    !employerId &&
    isDefined(associatedEnrollmentTeamId) &&
    isDefined(associatedEnrollmentTeamName);

  const crossEmployerSharedTeam = isCrossEmployerSharedTeam
    ? { id: associatedEnrollmentTeamId, name: associatedEnrollmentTeamName }
    : undefined;

  const { data: employerAgencies } = useMonolithQuery({
    method: 'agenciesControllerFindMany',
    params: employerId && [employerId],
  });
  const enrollmentTeams = employerAgencies?.enrollmentTeams ?? [];

  const availableEnrollmentTeams = crossEmployerSharedTeam
    ? [crossEmployerSharedTeam]
    : enrollmentTeams.map((enrollmentTeam) => ({
        id: enrollmentTeam.id,
        name: enrollmentTeam.name,
      }));

  const isSingleAvailableEnrollmentTeam = availableEnrollmentTeams.length === 1;
  const allRecordsHaveBenefit = recordsForActions.every((rec) => rec.benefitId);

  const { data: associatedOnboardingPeriodsSingleEmployee } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindMany',
    params: isSingleEmployee && [recordsForActions[0]?.employeeId],
  });

  const singleEmployeeOnboardingPeriod =
    associatedOnboardingPeriodsSingleEmployee?.find(
      (period) => period.id === recordsForActions[0].onboardingPeriodId
    );

  const { data: singlePeriodBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: singleEmployeeOnboardingPeriod?.id
      ? [singleEmployeeOnboardingPeriod.id]
      : false,
  });
  // using major medical only until supplementals are supported — should be replaced
  // when this moves to a benefit-dedicated UI that uses the correct benefit's enrollmentAgentId
  const majorMedicalBenefit = singlePeriodBenefits?.majorMedicalBenefit;

  const canAssignEnrollmentAgent = isSingleEmployee
    ? isDefined(majorMedicalBenefit)
    : allRecordsHaveBenefit;

  const {
    executeBatchCall: assignEnrollmentsTeam,
    errors: assignEnrollmentsTeamErrors,
  } = useBatchCallEndpoint({
    singularItemName: 'enrollment record',
    action: 'assigned',
    methodName: 'onboardingPeriodsControllerAssignEnrollmentTeam',
  });

  const {
    executeBatchCall: assignEnrollmentsAgent,
    errors: assignEnrollmentsAgentErrors,
  } = useBatchCallEndpoint({
    singularItemName: 'enrollment agent',
    action: 'assigned',
    methodName: 'benefitsControllerAssignEnrollmentAgent',
  });

  const currentEnrollmentTeamId =
    associatedEnrollmentTeamId ??
    singleEmployeeOnboardingPeriod?.enrollmentTeamId;
  const isEnrollmentTeamReadOnly =
    isSingleAvailableEnrollmentTeam &&
    (!currentEnrollmentTeamId ||
      availableEnrollmentTeams[0]?.id === currentEnrollmentTeamId);

  const form = useForm<AssignEnrollmentsFormFields>({
    mode: 'all',
    resolver: yupResolver(AssignEnrollmentSchema),
    defaultValues: {
      enrollmentTeamId:
        singleEmployeeOnboardingPeriod?.enrollmentTeamId ||
        (isSingleAvailableEnrollmentTeam
          ? availableEnrollmentTeams[0].id
          : undefined),
      enrollmentAgentId: singleEmployeeOnboardingPeriod?.enrollmentTeamId
        ? majorMedicalBenefit?.enrollmentAgentId
        : undefined,
    },
  });
  const { control, getValues, setValue, formState } = form;
  const { isValid } = formState;

  const submitAssignEnrollment = async (data: AssignEnrollmentsFormFields) => {
    const { enrollmentTeamId, enrollmentAgentId } = data;

    if (!enrollmentTeamId) {
      showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
      return;
    }

    await assignEnrollmentsTeam(
      recordsForActions.map(({ fullName, onboardingPeriodId }) => ({
        key: fullName,
        params: [onboardingPeriodId || '', { enrollmentTeamId }],
      })),
      { shouldAvoidPageReload: true }
    );

    if (isSingleEmployee && majorMedicalBenefit) {
      await assignEnrollmentsAgent(
        [
          {
            key: recordsForActions[0].fullName,
            params: [
              majorMedicalBenefit.id,
              { enrollmentAgentId: enrollmentAgentId || null },
            ],
          },
        ],
        { shouldAvoidPageReload: true, shouldShowNotifications: false }
      );
    } else if (!isSingleEmployee && allRecordsHaveBenefit) {
      await assignEnrollmentsAgent(
        recordsForActions.map(({ fullName, benefitId }) => ({
          key: fullName,
          params: [
            benefitId!,
            { enrollmentAgentId: enrollmentAgentId || null },
          ],
        })),
        { shouldAvoidPageReload: true, shouldShowNotifications: false }
      );
    }

    onClose();
  };

  const selectedEnrollmentTeamId = useWatch({
    control,
    name: 'enrollmentTeamId',
  });
  const { data: enrollmentAgents = [] } = useMonolithQuery({
    method: 'agentsControllerFindAllForAgency',
    params: selectedEnrollmentTeamId
      ? [selectedEnrollmentTeamId]
      : isSingleAvailableEnrollmentTeam && [availableEnrollmentTeams[0].id],
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: () => submitAssignEnrollment(getValues()),
          disabled: !isValid,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
          variant: 'tertiary',
        }}
        paperProps={{ pos: 'sticky' }}
      />
    ),
    [submitAssignEnrollment, getValues, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <>
      <form>
        <Stack p="0 40px 0 40px" mih="calc(100vh - 198px)">
          <>
            {isZorroOperations && (
              <Controller
                control={control}
                name="enrollmentTeamId"
                render={({ field: { onChange, ...rest } }) => (
                  <Select
                    {...rest}
                    required
                    searchable
                    label="Enrollment team"
                    placeholder="Select an enrollment team"
                    data={availableEnrollmentTeams.map((agency) => ({
                      label: agency.name,
                      value: agency.id,
                    }))}
                    onChange={(value) => {
                      onChange(value);
                      setValue('enrollmentAgentId', null);
                    }}
                    isReadOnly={isEnrollmentTeamReadOnly}
                  />
                )}
              />
            )}
            {canAssignEnrollmentAgent && (
              <Controller
                control={control}
                name="enrollmentAgentId"
                render={({ field }) => (
                  <Select
                    {...field}
                    searchable
                    clearable
                    label="Enrollment agent"
                    placeholder="Select an enrollment agent"
                    data={enrollmentAgents.map((agent) => ({
                      label: `${agent.firstName} ${agent.lastName} (${agent.email})`,
                      value: agent.id,
                    }))}
                    disabled={
                      !isSingleAvailableEnrollmentTeam &&
                      !selectedEnrollmentTeamId
                    }
                  />
                )}
              />
            )}
          </>
        </Stack>
      </form>

      <ErrorsTable
        tableTitle="Employee name"
        errors={[
          ...assignEnrollmentsTeamErrors,
          ...assignEnrollmentsAgentErrors,
        ]}
        isBulk={recordsForActions.length > 1}
      />
    </>
  );
};
